import { useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  NotificationApi,
  notificationApiRef,
} from '../../../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../../../apis/toolsProvisioningApi';
import { TestSuite } from '../interfaces/ui/testPlan';
import { mapTestSuiteResponse } from '../dto/responseDto/testSuite.response.dto';

export const useProjectTestSuites = () => {
  const notificationApi: NotificationApi = useApi(notificationApiRef);
  const toolsProvisioningApi = useApi(toolsProvisioningApiRef);

  const [testSuites, setTestSuites] = useState<TestSuite[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjectTestSuites = async (
    apmNumber: string,
    projectName: string,
    projectOrg: string,
    testPlanId: string,
    testPlanName: string,
  ) => {
    if (!apmNumber || !testPlanId) {
      setTestSuites([]);
      setLoading(false);

      return;
    }

    setLoading(true);

    try {
      const response = await toolsProvisioningApi.getAdoProjectTestSuites(
        apmNumber,
        projectName,
        projectOrg,
        testPlanId,
        testPlanName,
      );

      if (!response || response?.status?.toLowerCase() !== 'success') {
        throw new Error(`Failed to fetch mapped projects. Please try again.`);
      }

      const mappedTestSuites: TestSuite[] = mapTestSuiteResponse(response);
      if (mappedTestSuites.length === 0) {
        setError(
          `No test suites found for the selected project in ${testPlanName}. Please ensure that test suites exist and try again.`,
        );
        setTestSuites([]);
        return;
      }

      setTestSuites(mappedTestSuites);
      setError(null);
    } catch (error) {
      setTestSuites([]);
      setError(`Error occurred - ${error}`);
      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearTestSuitesFetchError = () => setError(null);

  return {
    fetchProjectTestSuites,
    testSuites,
    loadingTestSuites: loading,
    testSuitesError: error,
    clearTestSuitesFetchError,
  };
};
